import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import GetPrint from './GetPrint';
import { Card, CardContent } from '../components/ui/card';
import { FiLoader } from 'react-icons/fi';
import IconWrapper from '../components/print-steps/IconWrapper';
import { WhatsAppDocumentService } from '../services/WhatsAppDocumentService';
import toast from 'react-hot-toast';
import usePrintStore from '../store/printStore';
import { processFile } from '../utils/fileProcessing';
import { Document } from '../types/documentTypes';

const WhatsAppGetPrint: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const { addDocument } = usePrintStore();
  const [documentsProcessed, setDocumentsProcessed] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Memoize the document processing function
  const processDocument = useCallback(async (file: File, index: number) => {
    try {
      // Ensure file has proper MIME type
      let fileToProcess = file;
      const extension = file.name.split('.').pop()?.toLowerCase();
      
      if (!file.type || file.type === 'application/octet-stream') {
        if (extension === 'jpg' || extension === 'jpeg') {
          fileToProcess = new File([file], file.name, { type: 'image/jpeg' });
        } else if (extension === 'png') {
          fileToProcess = new File([file], file.name, { type: 'image/png' });
        } else if (extension === 'pdf') {
          fileToProcess = new File([file], file.name, { type: 'application/pdf' });
        }
      }
      
      const processedFile = await processFile(fileToProcess);
      
      // Create a consistent document ID that will match what's stored in IndexedDB
      const documentId = `whatsapp-${sessionId}-${index}-${Date.now()}`;
      
      const newDocument: Document = {
        id: documentId,
        name: processedFile.file.name,
        url: processedFile.url,
        file: processedFile.file,
        pageCount: processedFile.pageCount,
        source: 'whatsapp'
      };
      
      addDocument(newDocument);
      return true;
    } catch (error) {
      console.error('Error processing file:', error);
      return false;
    }
  }, [sessionId, addDocument]);

  useEffect(() => {
    if (!sessionId || documentsProcessed) return;
    
    let mounted = true;
    console.log(`User arriving with WhatsApp session ID: ${sessionId}`);
    
    // Create new AbortController for this effect instance
    abortControllerRef.current = new AbortController();
    
    const downloadWhatsAppDocuments = async () => {
      if (!mounted) return;
      setIsLoading(true);
      
      try {
        const result = await WhatsAppDocumentService.downloadWhatsAppDocuments(
          sessionId,
          abortControllerRef.current?.signal
        );
        
        if (!result.success || !mounted) {
          if (mounted) {
            toast.error('No documents found or download failed', {
              position: 'top-center',
              duration: 3000
            });
            setDocumentsProcessed(true);
          }
          return;
        }
        
        console.log(`Downloaded ${result.downloadedCount} WhatsApp documents`);
        let processedCount = 0;
        
        // Process each file with its index
        for (let i = 0; i < result.files.length; i++) {
          if (!mounted) break;
          const success = await processDocument(result.files[i], i);
          if (success) processedCount++;
        }
        
        if (mounted) {
          setDocumentsProcessed(true);
          
          if (processedCount > 0) {
            toast.success(`${processedCount} document${processedCount > 1 ? 's' : ''} loaded from WhatsApp`, {
              position: 'top-center',
              duration: 3000,
              id: 'whatsapp-documents-loaded'
            });
          }
          
          if (result.downloadedCount - processedCount > 0) {
            toast.error(`${result.downloadedCount - processedCount} document${result.downloadedCount - processedCount > 1 ? 's' : ''} failed to load`, {
              position: 'top-center',
              duration: 3000,
              id: 'whatsapp-documents-failed'
            });
          }
        }
      } catch (error) {
        if (mounted) {
          console.error('Error processing WhatsApp documents:', error);
          toast.error('Failed to load WhatsApp documents', {
            position: 'top-center',
            id: 'whatsapp-documents-error'
          });
          setDocumentsProcessed(true);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    downloadWhatsAppDocuments();
    
    return () => {
      mounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // Cancel any ongoing requests
        abortControllerRef.current = null;
      }
    };
  }, [sessionId, addDocument, documentsProcessed, processDocument]);

  return (
    <>
      {isLoading && (
        <div className="fixed top-20 left-0 right-0 z-50 flex justify-center px-4">
          <Card className="bg-blue-50 border-blue-200 max-w-md shadow-lg border">
            <CardContent className="p-4">
              <div className="flex items-center">
                <IconWrapper icon={FiLoader} className="text-blue-600 mr-3 text-lg animate-spin" />
                <div>
                  <p className="text-blue-800 text-sm">
                    Loading your documents...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <GetPrint whatsAppSession={sessionId}/>
    </>
  );
};

export default WhatsAppGetPrint;
